import { useEffect, useState, useCallback } from 'react';
import { useSnackbar } from 'notistack';
import { useLazyQuery } from '@apollo/client';
import { handleGraphQLError } from './utils';

function useNRLazyQuery({ query, options, qlObjKey, transformData, defaultValue, hideError = false }) {
  const [isCanceled, setIsCanceled] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [total, setTotal] = useState(0);
  const [result, setResult] = useState(null);

  const [customLoading, setCustomLoading] = useState(false);

  const [makeCall, { loading, error, data, called, startPolling, stopPolling, previousData }] = useLazyQuery(query, {
    ...options,
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
    skip: options.skipPollAttempt && options.skipPollAttempt(),
    onCompleted: data => {
      try {
        onCompleted(data);

        if (options?.onCompleted) {
          options.onCompleted(data);
        }
      } finally {
        setCustomLoading(false);
      }
    },
    onError: error => {
      setCustomLoading(false);
      if (options?.onError) {
        options.onError(error);
      }
    }
  });

  const handleMakeCall = useCallback(
    options => {
      setIsCanceled(false);
      setCustomLoading(true);
      if (defaultValue !== undefined) {
        setResult(defaultValue);
      }
      makeCall(options);
    },
    [makeCall, defaultValue]
  );

  function onCompleted(data) {
    if (!isCanceled && data && data[qlObjKey]) {
      if (data[qlObjKey].items) {
        setResult(!!transformData ? transformData(data[qlObjKey].items) : data[qlObjKey].items);
      } else {
        setResult(!!transformData ? transformData(data[qlObjKey]) : data[qlObjKey]);
      }
      if (data[qlObjKey].total || data[qlObjKey].total === 0) {
        setTotal(data[qlObjKey].total);
      }
    }
  }

  const handleCancel = useCallback(() => {
    setIsCanceled(true); // Mark request as canceled
    setCustomLoading(false);
  }, []);

  useEffect(() => {
    if (error) {
      // Process data if available even when there's an error
      if (data) {
        onCompleted(data);
      }
      if (!hideError) {
        handleGraphQLError({
          error,
          enqueueSnackbar,
          onFinally: () => setCustomLoading(false)
        });
      }
    }
  }, [error, data]);

  return [result, customLoading, called, handleMakeCall, total, error, startPolling, stopPolling, handleCancel, isCanceled];
}

export default useNRLazyQuery;
